import { useEffect, useRef, useState } from 'react';
import { Sparkles, Heart, Users, Flame, Music, UtensilsCrossed } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const storyPoints = [
  {
    icon: Sparkles,
    title: 'A Dream is Born',
    description: 'KPC Urban Korean Food was born from an unwavering passion to bring the authentic urban flavors of South Korea to Kissimmee, Florida. We are a young company, full of energy, with a clear vision: to make Korean cuisine accessible to everyone.',
    color: '#FFD700',
  },
  {
    icon: Flame,
    title: 'Authentic Urban Flavors',
    description: 'Inspired by the bustling streets of Myeongdong, the vibrant night markets of Hongdae, and the unmatched energy of Gangnam, we recreate that unique experience where tradition meets modernity.',
    color: '#DC143C',
  },
  {
    icon: UtensilsCrossed,
    title: 'Tradition in Every Dish',
    description: 'We deeply respect the roots of Korean cuisine. Every recipe honors traditional methods, authentic ingredients, and the flavors that have defined Korean gastronomy for generations.',
    color: '#FFD700',
  },
  {
    icon: Sparkles,
    title: 'Fresh & Modern Touch',
    description: "But we don't stop at tradition. We give classics a contemporary twist, fusing modern techniques with ancestral recipes. Our K-Bomb Fried Chicken and Tater Toast are proof of this innovation.",
    color: '#FFA500',
  },
  {
    icon: Users,
    title: 'For All Hearts',
    description: 'We believe good food has no age. From young people discovering K-pop and K-dramas, to adults seeking new culinary experiences — everyone is welcome at our table.',
    color: '#DC143C',
  },
  {
    icon: Music,
    title: 'More Than Food: It\'s an Experience',
    description: 'KPC is not just a restaurant; it is a piece of Seoul in Florida. We want you to feel the vibrant energy, the cozy atmosphere, and the joy of sharing a meal that transcends borders.',
    color: '#FFD700',
  },
];

export default function StorySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    const items = sectionRef.current?.querySelectorAll('.story-item');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="story" ref={sectionRef} className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Top glow */}
        <div 
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(ellipse, rgba(255, 215, 0, 0.4) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        {/* Bottom glow */}
        <div 
          className="absolute -bottom-40 right-0 w-[600px] h-[400px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(ellipse, rgba(220, 20, 60, 0.4) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        {/* Side glow */}
        <div 
          className="absolute top-1/2 -left-40 w-[400px] h-[600px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, rgba(255, 165, 0, 0.4) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <Badge className="mb-4 bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30 px-4 py-1">
            <Heart className="w-3 h-3 mr-2 inline" />
            Our Story
          </Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Orbitron']">
            <span className="text-white">From </span>
            <span className="gradient-text">Seoul</span>
            <span className="text-white"> to </span>
            <span className="gradient-text">Kissimmee</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            A journey of flavors, tradition, and passion that begins now
          </p>
        </div>

        {/* Story Introduction */}
        <div className="glass-card rounded-3xl p-8 lg:p-12 mb-16 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-[#FFD700]/30 rounded-tl-3xl" />
          <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-[#DC143C]/30 rounded-br-3xl" />
          
          <div className="relative z-10">
            <p className="text-xl sm:text-2xl lg:text-3xl text-white font-light leading-relaxed mb-6">
              <span className="text-[#FFD700] font-semibold">"</span>
              We are a <span className="text-[#FFD700] font-semibold">nascent</span> company, born from a love for Korea and its incredible culinary culture. 
              Our dream is simple yet powerful: 
              <span className="text-[#DC143C] font-semibold"> to bring the urban flavors of Seoul to your table</span>,
              with a fresh, modern touch that respects traditions.
              <span className="text-[#FFD700] font-semibold">"</span>
            </p>
            <div className="flex items-center justify-center gap-3 mt-8">
              <span className="w-12 h-[1px] bg-gradient-to-r from-transparent to-[#FFD700]/50" />
              <span className="text-[#FFD700] font-['Orbitron'] tracking-widest text-sm">KPC URBAN KOREAN FOOD</span>
              <span className="w-12 h-[1px] bg-gradient-to-l from-transparent to-[#FFD700]/50" />
            </div>
          </div>
        </div>

        {/* Story Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {storyPoints.map((point, index) => {
            const Icon = point.icon;
            const isVisible = visibleItems.includes(index);
            
            return (
              <div
                key={index}
                data-index={index}
                className={`story-item glass-card rounded-2xl p-6 lg:p-8 relative overflow-hidden group transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Hover glow effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 50% 0%, ${point.color}15 0%, transparent 70%)`,
                  }}
                />
                
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 relative z-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ 
                    background: `${point.color}15`,
                    boxShadow: `0 0 20px ${point.color}20`,
                  }}
                >
                  <Icon className="w-7 h-7" style={{ color: point.color }} />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3 font-['Orbitron'] relative z-10 group-hover:text-[#FFD700] transition-colors">
                  {point.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed relative z-10">
                  {point.description}
                </p>
                
                {/* Bottom accent line */}
                <div 
                  className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, ${point.color}, transparent)` }}
                />
              </div>
            );
          })}
        </div>

        {/* Closing Statement */}
        <div className="mt-16 lg:mt-20 text-center">
          <div className="inline-block glass-card rounded-2xl px-8 py-6 relative">
            {/* Corner decorations */}
            <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#FFD700]" />
            <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-[#DC143C]" />
            <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-[#DC143C]" />
            <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[#FFD700]" />
            
            <p className="text-lg sm:text-xl text-white italic">
              <span className="text-[#FFD700]">"</span>
              Come and be part of our story. 
              <span className="text-[#DC143C] font-semibold"> Welcome to the KPC family.</span>
              <span className="text-[#FFD700]">"</span>
            </p>
          </div>
          
          {/* Korean text */}
          <p className="mt-6 text-2xl sm:text-3xl text-[#FFD700]/30 font-bold tracking-widest">
            환영합니다
          </p>
          <p className="text-sm text-muted-foreground mt-1">Welcome</p>
        </div>
      </div>
    </section>
  );
}
